import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import { getMonth, getYear } from 'date-fns';

import styled, { css } from 'styled-components';
import { theme } from '../../../theme';
import {
  ExclamationTriangleIcon,
  InfoCircleIcon,
  SvgComponent,
} from '../../../assets';
import { BodySize, BodyText } from '../../typography';
import { Label as LabelComponent, LabelStyleProps } from '../label';
import {
  FieldStyleProps,
  FieldStyles,
  InputWrapperStyleProps,
  InputWrapperStyles,
  defaultInputHeight,
} from '../styles';
import { FieldIconSize } from '../field';
import { Alignment } from '../../../types';
import { floatLabel } from '../helpers';
import { Tooltip } from '../../tooltip';
import { ErrorBox } from '../errorBox';
import { DatePickerWrapper } from './DatePickerStyle';

/* eslint @typescript-eslint/no-var-requires: "off" */
const CreateRange = require('lodash');

type popperPlacementPosition =
  | 'bottom-start'
  | 'bottom-end'
  | 'top-start'
  | 'top-end';

type DatePickerFieldProps = {
  startDate: Date | null;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (d: Date | null) => void;
  alignUnit?: Alignment;
  errorMessage?: string;
  disabled?: boolean;
  label?: string;
  hasWrapperMarginBottom?: boolean;
  iconLeft?: SvgComponent;
  iconRight?: SvgComponent;
  iconSize?: FieldIconSize;
  isCompact?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  size?: BodySize;
  pattern?: string;
  unit?: string;
  value?: string | number;
  defaultValue?: string | number;
  controlled?: boolean;
  useParentState?: boolean;
  hasTooltip?: boolean;
  toolTipData?: string;
  dateFormat?: string;
  maxAge?: number;
  minAge?: number;
  popperPlacement?: popperPlacementPosition;
};
type FieldLabelProps = LabelStyleProps & {
  hasLeftIcon: boolean;
  hasLeftUnit: boolean;
};

type IconProps = {
  hasError?: boolean;
  size: FieldIconSize;
};

const unitWidth = '48px';

const Wrapper = styled.div<FieldStyleProps>`
  ${FieldStyles}
`;

const InputWrapper = styled.div<InputWrapperStyleProps>`
  ${InputWrapperStyles}
  height: ${defaultInputHeight};
`;

const Label = styled(LabelComponent)<FieldLabelProps>`
  ${({ float, hasLeftIcon }) =>
    hasLeftIcon &&
    !float &&
    `
      left: ${theme.spacings[7].rem};
      padding-left: ${theme.spacings[1].rem};
  `}

  ${({ hasLeftUnit, isCompact }) =>
    hasLeftUnit &&
    isCompact &&
    `
      left: ${unitWidth};
      padding-left: ${theme.spacings[3].rem};
    `}
`;

const UnitStyles = css`
  align-items: center;
  background-color: ${theme.colors.neutrals.solidGrays[7]};
  box-sizing: border-box;
  display: flex;
  flex: 0 0 ${unitWidth};
  font-weight: 700;
  justify-content: center;
`;

const LeftUnitWrapper = styled(BodyText)`
  ${UnitStyles}
  border-right: 1px solid ${theme.colors.neutrals.solidGrays[5]};
  border-radius: 0 2px 2px 0;
`;

const RightUnitWrapper = styled(BodyText)`
  ${UnitStyles}
  border-left: 1px solid ${theme.colors.neutrals.solidGrays[5]};
  border-radius: 0 0 2px 2px;
`;

const IconWrapperStyles = css<IconProps>`
  align-items: center;
  display: flex;

  svg {
    fill: ${({ hasError }) =>
      hasError
        ? theme.colors.system.systemRed
        : theme.colors.primary.graysGray};
    width: ${({ size }) => (size === 'md' ? 22 : 28)}px;
  }
`;

const LeftIconWrapper = styled.div<
  IconProps & {
    hasFloatingLabel?: boolean;
  }
>`
  ${IconWrapperStyles}
  margin-left: ${theme.spacings[3].rem};
  transition: margin-top 0.2s;

  ${({ hasFloatingLabel }) =>
    hasFloatingLabel &&
    `
      margin-top: ${theme.spacings[4].rem};
    `}
`;

const RightIconWrapper = styled.div<IconProps>`
  ${IconWrapperStyles}
  margin-right: ${theme.spacings[3].rem};
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const StyledInfoCircleIcon = styled(InfoCircleIcon)`
  margin-left: ${({ theme }) => theme.spacings[1].rem};
  fill: ${({ theme }) => theme.colors.neutrals.text.tertiary};
`;

const DateDropDownContainer = styled.div`
  margin: ${({ theme }) => theme.spacings[2].rem}
    ${({ theme }) => theme.spacings[1].rem};
`;

const StyledSelect = styled.select`
  padding: ${({ theme }) => theme.spacings[2].rem};
  margin: 0 ${({ theme }) => theme.spacings[1].rem};
  width: 45%;
  border: 0;
  border-right: ${({ theme }) => theme.spacings[2].rem} solid
    ${({ theme }) => theme.colors.primary.white};
`;

export const DatePickerField: React.FC<DatePickerFieldProps> = ({
  startDate,
  onFocus,
  onChange,
  alignUnit = 'left',
  errorMessage,
  disabled = false,
  label,
  hasWrapperMarginBottom = true,
  iconLeft: IconLeft,
  iconRight: IconRight,
  iconSize = 'md',
  isCompact = false,
  placeholder = 'DD/MM/YYYY',
  required = false,
  readOnly = false,
  unit,
  hasTooltip,
  toolTipData = 'Tooltip Text',
  dateFormat = 'dd/MM/yyyy',
  minAge,
  maxAge,
  popperPlacement = 'bottom-start',
}) => {
  const [isFocussed, setIsFocussed] = useState(false);

  const isLabelFloating = floatLabel(isCompact, isFocussed, false);

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocussed(true);
    onFocus && onFocus(event);
  };

  const getMinAndMaxDateForDob = () => {
    const currentDate = new Date();
    const currentDay = currentDate.getDate();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    const minYear = maxAge && currentYear - maxAge;
    const maxYear = minAge && currentYear - minAge;
    const minDate = new Date(`${minYear}/${currentMonth}/${currentDay}`);
    const maxDate = new Date(`${maxYear}/${currentMonth}/${currentDay}`);
    return { minDate, maxDate };
  };

  const years = CreateRange.range(
    getYear(new Date()) - 80,
    getYear(new Date()) - 17,
    1
  );

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return (
    <Wrapper haveMarginBottom={hasWrapperMarginBottom} isCompact={isCompact}>
      {label && (
        <Label
          hasLeftIcon={!!IconLeft}
          hasLeftUnit={alignUnit === 'left' && !!unit}
          float={isLabelFloating}
          isCompact={isCompact}
          required={required}
          value={label}
        />
      )}
      {hasTooltip && (
        <IconWrapper>
          <Tooltip
            customTrigger={<StyledInfoCircleIcon width={24} />}
            text={toolTipData}
          ></Tooltip>
        </IconWrapper>
      )}
      <InputWrapper
        disabled={disabled}
        hasError={!!errorMessage}
        isFocussed={isFocussed}
        readOnly={readOnly}
      >
        {unit && alignUnit === 'left' && (
          <LeftUnitWrapper>{unit}</LeftUnitWrapper>
        )}
        {IconLeft && (
          <LeftIconWrapper hasFloatingLabel={isLabelFloating} size={iconSize}>
            <IconLeft />
          </LeftIconWrapper>
        )}

        <DatePickerWrapper>
          <DatePicker
            renderCustomHeader={({ date, changeYear, changeMonth }) => (
              <DateDropDownContainer>
                <StyledSelect
                  value={getYear(date)}
                  onChange={({ target: { value } }) =>
                    changeYear(parseInt(value))
                  }
                >
                  {years
                    .sort((a: number, b: number) => b - a)
                    .map((option: number) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                </StyledSelect>

                <StyledSelect
                  value={months[getMonth(date)]}
                  onChange={({ target: { value } }) =>
                    changeMonth(months.indexOf(value))
                  }
                >
                  {months.map((option: string) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </StyledSelect>
              </DateDropDownContainer>
            )}
            customInput={<input inputMode="none" />}
            disabled={disabled}
            showPopperArrow={false}
            popperPlacement={popperPlacement}
            placeholderText={placeholder}
            dateFormat={dateFormat}
            selected={startDate}
            onChange={onChange}
            onFocus={handleFocus}
            onKeyDown={(event: React.KeyboardEvent) => {
              const element = event.currentTarget as HTMLInputElement;
              element.value = element.value
                .replace(/^(\d\d)(\d)$/g, '$1/$2')
                .replace(/^(\d\d\/\d\d)(\d+)$/g, '$1/$2')
                .replace(/[^\d\/]/g, '');
              if (!/^[0-9]+$/.test(event.key) && !(event.key == 'Backspace')) {
                event.preventDefault();
              }
            }}
            minDate={minAge ? getMinAndMaxDateForDob().minDate : undefined}
            maxDate={maxAge ? getMinAndMaxDateForDob().maxDate : undefined}
            popperProps={{ strategy: 'fixed' }}
          />
        </DatePickerWrapper>
        {(errorMessage || IconRight) && (
          <RightIconWrapper hasError={!!errorMessage} size={iconSize}>
            {errorMessage ? (
              <ExclamationTriangleIcon />
            ) : (
              IconRight && <IconRight />
            )}
          </RightIconWrapper>
        )}
        {unit && alignUnit === 'right' && (
          <RightUnitWrapper>{unit}</RightUnitWrapper>
        )}
      </InputWrapper>
      {errorMessage && <ErrorBox size="sm">{errorMessage}</ErrorBox>}
    </Wrapper>
  );
};
